import React, { useEffect, useState } from 'react';
import { css } from '@emotion/core';
import {
  Heading,
  Text,
  ContentLoader,
  useFlyoutDialog,
  useSlideFadeTransition,
  IconButton,
  XIcon,
  SecondaryButton,
  PrimaryButton,
} from '@weave/design-system';
import { theme } from '@weave/theme';
import {
  Migration,
  Service,
  RegisteredService,
} from '@weave/schema-gen-ts/dist/schemas/organization/merge/coordinator/v1/api.pb';
import { getMigrationDetails, listServices, getOrgInfo } from './api';

interface MigrationDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  migrationId?: string;
  registeredServices: RegisteredService[];
  onRetryClick: (migrationId: string, isRollbackFailed: boolean) => void;
  onRollbackClick: (migrationId: string) => void;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const getServiceName = (
  registeredServiceId: string,
  registeredServices: RegisteredService[]
): string => {
  const service = registeredServices.find(
    (s) => s.registeredServiceId === registeredServiceId
  );
  return service?.serviceName || registeredServiceId;
};

const drawerStyles = css`
  position: fixed !important;
  top: 52px;
  right: 0;
  bottom: 0;
  width: 800px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.heavy};
  z-index: ${theme.zIndex.higher};
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const headerStyles = css`
  padding: ${theme.spacing(3)};
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const contentStyles = css`
  padding: ${theme.spacing(3)};
  flex: 1;
  overflow-y: auto;
`;

export const MigrationDetailsDrawer: React.FC<MigrationDetailsDrawerProps> = ({
  isOpen,
  onClose,
  migrationId = '',
  registeredServices,
  onRetryClick,
  onRollbackClick,
}) => {
  const [migration, setMigration] = useState<Migration | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [sourceOrgName, setSourceOrgName] = useState<string | null>(null);
  const [destOrgName, setDestOrgName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { active, FlyoutDialog, flyoutHeaderProps, flyoutProps, triggerProps } =
    useFlyoutDialog({});
  const transitions = useSlideFadeTransition({ active, direction: 'left' });

  const fetchDetails = async () => {
    if (!migrationId) return;

    setLoading(true);
    setError(null);
    setSourceOrgName(null);
    setDestOrgName(null);

    try {
      const [migrationData, servicesData] = await Promise.all([
        getMigrationDetails(migrationId),
        listServices(migrationId),
      ]);

      setMigration(migrationData);
      setServices(servicesData.services || []);

      // Fetch org names
      if (migrationData.sourceOrgId || migrationData.destinationOrgId) {
        if (migrationData.sourceOrgId) {
          try {
            const sourceOrg = await getOrgInfo(migrationData.sourceOrgId);
            setSourceOrgName(sourceOrg.name || migrationData.sourceOrgId);
          } catch (err) {
            console.warn('Failed to fetch source org name:', err);
            setSourceOrgName(migrationData.sourceOrgId);
          }
        }
        if (migrationData.destinationOrgId) {
          try {
            const destOrg = await getOrgInfo(migrationData.destinationOrgId);
            setDestOrgName(destOrg.name || migrationData.destinationOrgId);
          } catch (err) {
            console.warn('Failed to fetch destination org name:', err);
            setDestOrgName(migrationData.destinationOrgId);
          }
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load migration details';
      setError(errorMessage);
      console.error('Error fetching migration details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchDetails();
  };

  // Sync the isOpen prop with the flyout dialog state
  useEffect(() => {
    if (isOpen && !active && migrationId) {
      triggerProps.onClick?.();
    } else if (!isOpen && active) {
      triggerProps.onClick?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, migrationId]);

  // Notify parent when drawer is closed (including when clicking outside)
  useEffect(() => {
    if (!active && isOpen) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (active && migrationId) {
      fetchDetails();
    }
  }, [active, migrationId]);

  const handleClose = () => {
    triggerProps.onClick?.();
    onClose();
  };

  const sortedServices = [...services].sort((a, b) => {
    const waveA = a.mergeWave || 0;
    const waveB = b.mergeWave || 0;
    return waveA - waveB;
  });

  return (
    <>
      {active &&
        transitions.map(
          ({ key, item, props }) =>
            item && (
              <FlyoutDialog key={key} style={props} {...flyoutProps} css={drawerStyles}>
                <div css={headerStyles}>
                  <Heading level={2} {...flyoutHeaderProps}>
                    Migration Details
                  </Heading>
                  <IconButton label="Close drawer" onClick={handleClose}>
                    <XIcon />
                  </IconButton>
                </div>

                <div css={contentStyles}>
                  {loading ? (
                    <ContentLoader />
                  ) : error ? (
                    <div
                      css={css`
                        text-align: center;
                      `}
                    >
                      <Text color="error">{error}</Text>
                      <SecondaryButton
                        onClick={handleRetry}
                        css={css`
                          margin-top: ${theme.spacing(2)};
                        `}
                      >
                        Retry
                      </SecondaryButton>
                    </div>
                  ) : migration ? (
                    <div>
                      {/* Organization & Status Section */}
                      <div
                        css={css`
                          margin-bottom: ${theme.spacing(4)};
                          padding-bottom: ${theme.spacing(3)};
                          border-bottom: 1px solid #e0e0e0;
                        `}
                      >
                        <Heading level={3}>Organizations</Heading>
                        <div
                          css={css`
                            margin: ${theme.spacing(2, 0)};
                          `}
                        >
                          <div
                            css={css`
                              display: flex;
                              justify-content: space-between;
                              padding: ${theme.spacing(1.5, 0)};
                              border-bottom: 1px solid #f5f5f5;
                            `}
                          >
                            <Text as="label" weight="bold" size="small" color="light">
                              Source Org Name
                            </Text>
                            <Text>{sourceOrgName || '-'}</Text>
                          </div>
                          <div
                            css={css`
                              display: flex;
                              justify-content: space-between;
                              padding: ${theme.spacing(1.5, 0)};
                              border-bottom: 1px solid #f5f5f5;
                            `}
                          >
                            <Text as="label" weight="bold" size="small" color="light">
                              Source Org ID
                            </Text>
                            <Text>{migration.sourceOrgId || '-'}</Text>
                          </div>
                          <div
                            css={css`
                              display: flex;
                              justify-content: space-between;
                              padding: ${theme.spacing(1.5, 0)};
                              border-bottom: 1px solid #f5f5f5;
                            `}
                          >
                            <Text as="label" weight="bold" size="small" color="light">
                              Destination Org Name
                            </Text>
                            <Text>{destOrgName || '-'}</Text>
                          </div>
                          <div
                            css={css`
                              display: flex;
                              justify-content: space-between;
                              padding: ${theme.spacing(1.5, 0)};
                              border-bottom: 1px solid #f5f5f5;
                            `}
                          >
                            <Text as="label" weight="bold" size="small" color="light">
                              Destination Org ID
                            </Text>
                            <Text>{migration.destinationOrgId || '-'}</Text>
                          </div>
                        </div>

                        <div
                          css={css`
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-bottom: ${theme.spacing(2)};
                          `}
                        >
                          <Heading level={3}>Migration Status</Heading>
                          <div
                            css={css`
                              display: flex;
                              gap: ${theme.spacing(1)};
                            `}
                          >
                            <SecondaryButton
                              onClick={() => {
                                if (!migration?.migrationId) return;
                                const isRollbackFailed =
                                  migration.state === 'ROLLBACK_FAILED';
                                onRetryClick(migration.migrationId, isRollbackFailed);
                              }}
                              disabled={
                                !migration ||
                                (migration.state !== 'MERGE_FAILED' &&
                                  migration.state !== 'ROLLBACK_FAILED')
                              }
                            >
                              {migration?.state === 'ROLLBACK_FAILED'
                                ? 'Retry Rollback'
                                : 'Retry Migration'}
                            </SecondaryButton>
                            <PrimaryButton
                              onClick={() => {
                                if (!migration?.migrationId) return;
                                onRollbackClick(migration.migrationId);
                              }}
                              disabled={
                                !migration ||
                                (migration.state !== 'MERGE_FAILED' &&
                                  migration.state !== 'MERGE_COMPLETED')
                              }
                              css={css`
                                background-color: ${theme.colors.critical10};
                                color: ${theme.colors.critical50};
                                border: 1px solid ${theme.colors.critical50};
                                &:hover:not(:disabled) {
                                  background-color: ${theme.colors.critical20};
                                }
                              `}
                            >
                              Rollback Migration
                            </PrimaryButton>
                          </div>
                        </div>
                        <div
                          css={css`
                            margin: ${theme.spacing(2, 0)};
                          `}
                        >
                          <div
                            css={css`
                              display: flex;
                              justify-content: space-between;
                              padding: ${theme.spacing(1.5, 0)};
                              border-bottom: 1px solid #f5f5f5;
                            `}
                          >
                            <Text as="label" weight="bold" size="small" color="light">
                              State
                            </Text>
                            <div
                              css={css`
                                display: inline-block;
                                padding: ${theme.spacing(1, 1.5)};
                                background-color: ${migration.state ===
                                  'MERGE_COMPLETED' ||
                                migration.state === 'ROLLBACK_COMPLETED'
                                  ? theme.colors.success10
                                  : migration.state === 'MERGE_PROCESSING' ||
                                    migration.state === 'ROLLBACK_PROCESSING'
                                  ? theme.colors.warning5
                                  : migration.state === 'MERGE_FAILED' ||
                                    migration.state === 'ROLLBACK_FAILED'
                                  ? theme.colors.critical20
                                  : theme.colors.primary10};
                                border-radius: 4px;
                                font-weight: 600;
                              `}
                            >
                              {migration.state || '-'}
                            </div>
                          </div>
                          <div
                            css={css`
                              display: flex;
                              justify-content: space-between;
                              padding: ${theme.spacing(1.5, 0)};
                              border-bottom: 1px solid #f5f5f5;
                            `}
                          >
                            <Text as="label" weight="bold" size="small" color="light">
                              Requested
                            </Text>
                            <Text>{formatDate(migration.createTime)}</Text>
                          </div>
                          {migration.completionTime && (
                            <div
                              css={css`
                                display: flex;
                                justify-content: space-between;
                                padding: ${theme.spacing(1.5, 0)};
                                border-bottom: 1px solid #f5f5f5;
                              `}
                            >
                              <Text as="label" weight="bold" size="small" color="light">
                                Completed
                              </Text>
                              <Text>{formatDate(migration.completionTime)}</Text>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Services Section */}
                      <div>
                        <Heading level={3}>Services ({sortedServices.length})</Heading>
                        {sortedServices.length === 0 ? (
                          <Text color="light">No services for this migration</Text>
                        ) : (
                          <div
                            css={css`
                              display: flex;
                              flex-direction: column;
                              gap: ${theme.spacing(2)};
                              margin-top: ${theme.spacing(2)};
                            `}
                          >
                            {sortedServices.map((service) => (
                              <div
                                key={service.serviceId}
                                css={css`
                                  padding: ${theme.spacing(2)};
                                  border: 1px solid #e0e0e0;
                                  border-radius: 4px;
                                  background-color: #fafafa;
                                `}
                              >
                                <div
                                  css={css`
                                    display: flex;
                                    justify-content: space-between;
                                    align-items: start;
                                    margin-bottom: ${theme.spacing(1)};
                                  `}
                                >
                                  <div>
                                    <Text weight="bold">
                                      {getServiceName(
                                        service.registeredServiceId || '',
                                        registeredServices
                                      )}
                                    </Text>
                                    <Text size="small" color="light">
                                      Wave {service.mergeWave || 0}
                                    </Text>
                                  </div>
                                  <div
                                    css={css`
                                      padding: ${theme.spacing(0.5, 1)};
                                      background-color: ${service.state ===
                                        'MERGE_COMPLETED' ||
                                      service.state === 'ROLLBACK_COMPLETED'
                                        ? theme.colors.success10
                                        : service.state === 'MERGE_PROCESSING' ||
                                          service.state === 'ROLLBACK_PROCESSING'
                                        ? theme.colors.warning5
                                        : service.state === 'MERGE_FAILED' ||
                                          service.state === 'ROLLBACK_FAILED'
                                        ? theme.colors.critical20
                                        : theme.colors.primary10};
                                      border-radius: 3px;
                                      font-weight: 600;
                                      font-size: 12px;
                                    `}
                                  >
                                    {service.state || '-'}
                                  </div>
                                </div>

                                {service.completionTime && (
                                  <Text size="small" color="light">
                                    Completed: {formatDate(service.completionTime)}
                                  </Text>
                                )}

                                {service.errorMessage && (
                                  <div
                                    css={css`
                                      margin-top: ${theme.spacing(1)};
                                      padding: ${theme.spacing(1)};
                                      background-color: ${theme.colors.critical5};
                                      border-left: 3px solid ${theme.colors.critical50};
                                      border-radius: 2px;
                                    `}
                                  >
                                    <Text size="small" color="error">
                                      {service.errorMessage}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </FlyoutDialog>
            )
        )}
    </>
  );
};
